import type {
  AgentInfo,
  AgentMemory,
  AgentModel,
  AgentSkill,
  AgentsCatalog,
  AgentTool,
  MemoryItem,
  MemoryList,
  SkillDoc,
  SkillDraft,
  SkillHistory,
  SkillRefusal,
  SkillVersion,
} from './types';
import { str } from './wireValues';

// Wire readers for the Agents view endpoints (Plan 5). Wire data is untrusted:
// malformed entries are dropped, never thrown on.

type Obj = Record<string, unknown>;

const obj = (v: unknown): Obj | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : null;
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const present = <T>(v: T | null): v is T => v !== null;

const toModel = (v: unknown): AgentModel | null => {
  const o = obj(v);
  if (!o) return null;
  return {
    provider: str(o.provider) ?? '',
    kind: str(o.kind) ?? '',
    vendor: str(o.vendor) ?? '',
    model: str(o.model) ?? '',
  };
};

const toSkill = (v: unknown): AgentSkill | null => {
  const o = obj(v);
  const name = o && str(o.name);
  if (!o || !name) return null;
  return {
    name,
    description: str(o.description) ?? '',
    hash: str(o.hash) ?? '',
    version: str(o.version) ?? '',
    matchesRepo: o.matchesRepo !== false,
  };
};

const toTool = (v: unknown): AgentTool | null => {
  const o = obj(v);
  const name = o && str(o.name);
  if (!o || !name) return null;
  return { name, label: str(o.label) ?? name };
};

const toMemory = (v: unknown): AgentMemory => {
  const o = obj(v) ?? {};
  return {
    history: o.history === true,
    preferences: o.preferences === true,
    caseNotes: o.caseNotes === true,
    note: str(o.note) ?? '',
  };
};

const toAgent = (v: unknown): AgentInfo | null => {
  const o = obj(v);
  const name = o && str(o.name);
  if (!o || !name) return null;
  return {
    name,
    label: str(o.label) ?? name,
    tag: str(o.tag) ?? null,
    kind: o.kind === 'code' ? 'code' : 'llm',
    role: str(o.role) ?? '',
    model: toModel(o.model),
    instructionsHash: str(o.instructionsHash) ?? null,
    skills: list(o.skills).map(toSkill).filter(present),
    tools: list(o.tools).map(toTool).filter(present),
    memory: toMemory(o.memory),
    canSend: o.canSend === true,
  };
};

export function toAgentsCatalog(body: unknown): AgentsCatalog | null {
  const o = obj(body);
  if (!o || !Array.isArray(o.agents)) return null;
  return {
    gitSha: str(o.gitSha) ?? '',
    skillsStale: o.skillsStale === true,
    skillsBackend:
      o.skillsBackend === 'repo' || o.skillsBackend === 'foundry'
        ? o.skillsBackend
        : undefined,
    agents: o.agents.map(toAgent).filter(present),
  };
}

export function toSkillDoc(body: unknown): SkillDoc | null {
  const o = obj(body);
  const agent = o && str(o.agent);
  const name = o && str(o.name);
  const text = o ? str(o.body) : undefined;
  if (!o || !agent || !name || text === undefined) return null;
  return {
    agent,
    name,
    description: str(o.description) ?? '',
    hash: str(o.hash) ?? '',
    body: text,
    version: str(o.version) ?? '',
    text: str(o.text) ?? text,
    canPublish: o.canPublish === true,
    myDraft: toSkillDraft(o.myDraft),
    stale: o.stale === true,
  };
}

export function toSkillVersion(v: unknown): SkillVersion | null {
  const o = obj(v);
  const version = o && str(o.version);
  const author = o && str(o.author);
  if (!o || !version || !author) return null;
  return {
    version,
    kind: o.kind === 'draft' ? 'draft' : 'published',
    author,
    authorName: str(o.authorName) ?? null,
    at: str(o.at) ?? '',
    reason: str(o.reason) ?? null,
    basedOn: str(o.basedOn) ?? null,
    copiedFrom: str(o.copiedFrom) ?? null,
    live: o.live === true,
  };
}

export function toSkillDraft(v: unknown): SkillDraft | null {
  const base = toSkillVersion(v);
  const text = base ? str(obj(v)?.text) : undefined;
  if (!base || text === undefined) return null;
  return { ...base, kind: 'draft', text };
}

export function toSkillHistory(body: unknown): SkillHistory | null {
  const o = obj(body);
  const agent = o && str(o.agent);
  const name = o && str(o.name);
  if (!o || !agent || !name) return null;
  return {
    agent,
    name,
    live: str(o.live) ?? '',
    canPublish: o.canPublish === true,
    published: list(o.published).map(toSkillVersion).filter(present),
    drafts: list(o.drafts).map(toSkillDraft).filter(present),
    stale: o.stale === true,
  };
}

/** A refusal body is `{detail: "<code>"}` plus `found` / `live`; anything else is not one. */
export function toSkillRefusal(body: unknown): SkillRefusal | null {
  const o = obj(body);
  const code = o && str(o.detail);
  if (!o || !code) return null;
  const found = list(o.found).flatMap((f) => {
    const e = obj(f);
    const kind = e && str(e.kind);
    const line = e?.line;
    return kind && typeof line === 'number' ? [{ kind, line }] : [];
  });
  return { code, found, live: str(o.live) ?? null };
}

const toItem = (v: unknown): MemoryItem | null => {
  const o = obj(v);
  const id = o && str(o.id);
  if (!o || !id) return null;
  return { id, text: str(o.text) ?? '' };
};

export function toMemoryList(body: unknown): MemoryList | null {
  const o = obj(body);
  const agent = o && str(o.agent);
  if (!o || !agent || !Array.isArray(o.items)) return null;
  return {
    agent,
    enabled: o.enabled === true,
    items: o.items.map(toItem).filter(present),
  };
}
